import React from 'react';
import { donutSegments, sparkPoints } from './cartAnalyticsFormat';

// Small dependency-free SVG charts for the Cart Analytics page.

export const COLORS = {
  blue: '#2f6fed',
  green: '#1f9d6b',
  amber: '#e9a23b',
  red: '#d9534f',
  grey: '#aeb6c2',
  teal: '#17a2b8',
};

// A tiny trend line under a KPI value.
export function Sparkline({ values, color = COLORS.blue, width = 120, height = 32 }) {
  if (!values || values.length < 2) return <div className="ca-spark-empty" style={{ height }} />;
  const points = sparkPoints(values, width, height - 4);
  const area = `0,${height - 2} ${points.split(' ').map((p) => { const [x, y] = p.split(','); return `${x},${Number(y) + 2}`; }).join(' ')} ${width},${height - 2}`;
  return (
    <svg className="ca-spark" viewBox={`0 0 ${width} ${height}`} width="100%" height={height} preserveAspectRatio="none" role="img" aria-label="trend">
      <polygon points={area} fill={color} opacity="0.12" />
      <polyline points={points.split(' ').map((p) => { const [x, y] = p.split(','); return `${x},${Number(y) + 2}`; }).join(' ')} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

// Donut with a centre label and a legend.
export function Donut({
  parts, size = 132, thickness = 18, centerLabel, centerSub, formatValue = (v) => String(v),
}) {
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  const segs = donutSegments(parts, c);
  return (
    <div className="ca-donut">
      <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} role="img" aria-label={centerLabel || 'chart'}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#eef0f3" strokeWidth={thickness} />
        {segs.map((s) => (
          <circle
            key={s.label}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={thickness}
            strokeDasharray={`${s.length} ${c - s.length}`}
            strokeDashoffset={-s.offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          >
            <title>{`${s.label}: ${formatValue(s.value)} (${s.pct.toFixed(1)}%)`}</title>
          </circle>
        ))}
        {centerLabel && <text x="50%" y={centerSub ? '47%' : '53%'} textAnchor="middle" className="ca-donut-center">{centerLabel}</text>}
        {centerSub && <text x="50%" y="62%" textAnchor="middle" className="ca-donut-sub">{centerSub}</text>}
      </svg>
      <ul className="ca-legend">
        {(parts || []).map((p) => (
          <li key={p.label}>
            <span className="ca-legend-dot" style={{ background: p.color }} />
            <span className="ca-legend-label">{p.label}</span>
            <span className="ca-legend-value">{formatValue(p.value)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Horizontal funnel: total carts, then each outcome as a bar proportional to its share.
export function FunnelBars({ funnel }) {
  const tone = { completed: COLORS.green, incomplete: COLORS.amber, saved: COLORS.grey };
  return (
    <div className="ca-funnel">
      <div className="ca-funnel-total">
        <span>All carts</span>
        <strong>{funnel.total.toLocaleString('en-US')}</strong>
      </div>
      {funnel.stages.map((s) => (
        <div key={s.key} className="ca-funnel-row">
          <div className="ca-funnel-head">
            <span>{s.label}</span>
            <span>{s.value.toLocaleString('en-US')} <small>({s.pct.toFixed(1)}%)</small></span>
          </div>
          <div className="ca-funnel-track">
            <div className="ca-funnel-bar" style={{ width: `${Math.min(100, s.pct)}%`, background: tone[s.key] }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// A rate shown as a filled bar with its value, e.g. conversion or abandonment.
export function GaugeBar({ label, pct, tone = COLORS.blue, hint }) {
  const v = Number.isFinite(Number(pct)) ? Math.max(0, Math.min(100, Number(pct))) : 0;
  return (
    <div className="ca-gauge" title={hint || ''}>
      <div className="ca-gauge-head">
        <span>{label}</span>
        <strong>{v.toFixed(1)}%</strong>
      </div>
      <div className="ca-gauge-track">
        <div className="ca-gauge-fill" style={{ width: `${v}%`, background: tone }} />
      </div>
    </div>
  );
}
